import {
  useCreateUnit,
  useDeleteUnit,
  useListResidents,
  useListUnits,
  useUpdateUnit,
  type UnitIn,
  type UnitOut,
} from '@myrentalmate/api-client';
import { Chip } from '@mui/material';
import { useMemo } from 'react';

import {
  optional,
  optionalNumber,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { listResult, okItems } from '../../lib/apiData';
import { formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RecordExtras } from '../records/RecordExtras';

const STATUS_LABELS: Record<UnitOut['status'], string> = {
  vacant: 'Vacant',
  occupied: 'Occupied',
  unavailable: 'Unavailable',
};

const FIELDS: Field[] = [
  { name: 'unit_number', label: 'Unit', required: true },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    defaultValue: 'vacant',
    options: Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
  },
  { name: 'bedrooms', label: 'Bedrooms', type: 'number' },
  { name: 'bathrooms', label: 'Bathrooms', type: 'number' },
  { name: 'square_feet', label: 'Square Feet', type: 'number' },
  { name: 'market_rent', label: 'Market Rent', type: 'number' },
];

const COLUMNS: Column<UnitOut>[] = [
  { label: 'Unit', render: (u) => u.unit_number },
  {
    label: 'Status',
    render: (u) => (
      <Chip
        size="small"
        label={STATUS_LABELS[u.status]}
        color={u.status === 'occupied' ? 'success' : 'default'}
      />
    ),
  },
  {
    label: 'Bed / bath',
    render: (u) => `${u.bedrooms ?? '–'} / ${u.bathrooms === null ? '–' : Number(u.bathrooms)}`,
  },
  { label: 'Sq ft', render: (u) => u.square_feet?.toLocaleString() ?? '–' },
  {
    label: 'Market rent',
    render: (u) => formatMoney(u.market_rent),
  },
];

function toValues(u: UnitOut): FormValues {
  return {
    unit_number: u.unit_number,
    status: u.status,
    bedrooms: u.bedrooms?.toString() ?? '',
    bathrooms: u.bathrooms === null ? '' : String(Number(u.bathrooms)),
    square_feet: u.square_feet?.toString() ?? '',
    market_rent: u.market_rent === null ? '' : Number(u.market_rent).toFixed(2),
  };
}

export function UnitsSection({ propertyId }: { propertyId: number }) {
  const canManage = useCan('properties.manage');
  const create = useCreateUnit();
  const update = useUpdateUnit();
  const remove = useDeleteUnit();
  const residents = useListResidents({ property_id: propertyId, page_size: 200 });
  const columns = useMemo(() => {
    const names = new Map<number, string[]>();
    for (const r of okItems(residents.data)) {
      if (r.unit_id !== null) {
        names.set(r.unit_id, [...(names.get(r.unit_id) ?? []), `${r.first_name} ${r.last_name}`]);
      }
    }
    const resident: Column<UnitOut> = {
      label: 'Resident',
      render: (u) => names.get(u.id)?.join(', ') ?? '–',
    };
    return [...COLUMNS.slice(0, 2), resident, ...COLUMNS.slice(2)];
  }, [residents.data]);

  function toBody(values: FormValues): UnitIn {
    return {
      property_id: propertyId,
      unit_number: required(values, 'unit_number'),
      status: required(values, 'status') as UnitIn['status'],
      bedrooms: optionalNumber(values, 'bedrooms'),
      bathrooms: optional(values, 'bathrooms'),
      square_feet: optionalNumber(values, 'square_feet'),
      market_rent: optional(values, 'market_rent'),
    };
  }

  function useRows({ q, page, pageSize }: ListParams) {
    return listResult(
      useListUnits({
        property_id: propertyId,
        q,
        page,
        page_size: pageSize,
      }),
    );
  }

  return (
    <RecordPage
      title="Units"
      singular="unit"
      fields={FIELDS}
      columns={columns}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) => update.mutateAsync({ recordId, data: toBody(values) })}
      remove={(recordId) => remove.mutateAsync({ recordId })}
      renderExtras={(row) => (
        <RecordExtras entityType="unit" entityId={row.id} canManage={canManage} />
      )}
    />
  );
}
